import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Stack } from '@mui/material';
import PageLayout from '../components/global/PageLayout';
import PageLoading from '../components/global/PageLoading';
import MultiSelectFilter from '../components/filtering/MultiSelectFilter';
import CancellationWaitlistSettingsFormModal from '../components/cancellation-waitlist/CancellationWaitlistSettingsFormModal';
import WaitlistTable from '../components/cancellation-waitlist/WaitlistTable';
import WaitlistEmptyState from '../components/cancellation-waitlist/WaitlistEmptyState';
import WaitlistEntrySideBarModal from '../components/cancellation-waitlist/WaitlistEntrySideBarModal';
import ManualNotifyDialog from '../components/cancellation-waitlist/ManualNotifyDialog';
import useUser from '../hooks/global/useUser';
import useConfirm from '../hooks/useConfirm';
import useWaitlist from '../hooks/cancellation-waitlist/useWaitlist';
import useRemoveWaitlistMember from '../hooks/cancellation-waitlist/useRemoveWaitlistMember';
import { getArtistName, isUserAdminOrManager } from '../utils/global/helper';
import { LoadedUser } from '../interfaces';
import { CancellationWaitlistEntryRow } from '../types/cancellation-waitlist';

export default function CancellationWaitlistPage() {
  const { user } = useUser<LoadedUser>();
  const confirm = useConfirm();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [notifyEntryId, setNotifyEntryId] = useState<string | null>(null);
  // null = untouched (defaults to everyone selected); empty = falls back to all.
  const [selectedArtistIds, setSelectedArtistIds] = useState<string[] | null>(
    null,
  );

  const isManagerView =
    !!user && user.business.type === 'STUDIO' && isUserAdminOrManager(user);

  const artistOptions = useMemo(() => {
    if (!user || !isManagerView) return [];
    return user.business.employees
      .map(etb => ({
        value: etb.employeeId,
        label: getArtistName(etb) || 'Unnamed',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [user, isManagerView]);

  const artistFilterState = useMemo(
    () => selectedArtistIds ?? artistOptions.map(option => option.value),
    [selectedArtistIds, artistOptions],
  );

  const filteredToArtists =
    isManagerView &&
    artistFilterState.length > 0 &&
    artistFilterState.length < artistOptions.length;

  const employeeIds = useMemo(() => {
    if (!user) return [];
    if (filteredToArtists) return [...artistFilterState].sort();
    if (isManagerView) {
      // Sorted so the query key stays stable across roster order changes.
      return user.business.employees
        .map(employee => employee.employeeId)
        .sort();
    }
    return [user.userId];
  }, [user, isManagerView, filteredToArtists, artistFilterState]);

  const {
    entries,
    totalCount,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useWaitlist({
    businessId: user?.business.id,
    employeeIds,
  });

  const onEndReached = async () => {
    if (!hasNextPage || isFetching) return;
    await fetchNextPage();
  };

  const { mutate: removeMember } = useRemoveWaitlistMember({
    onSuccess: () => {
      toast.success('Removed from the waitlist.');
      setSelectedEntryId(null);
    },
    onError: (message: string) => {
      toast.error(message);
    },
  });

  const onRemove = async (
    entry: Pick<
      CancellationWaitlistEntryRow,
      'waitlistEntryId' | 'customerDisplayName'
    >,
  ) => {
    const confirmed = await confirm({
      title: 'Remove from waitlist?',
      message: `${entry.customerDisplayName} will no longer be notified about openings.`,
      positiveText: 'Remove',
      negativeText: 'Cancel',
    });
    if (!confirmed) return;
    removeMember(entry.waitlistEntryId);
  };

  // Only the unfiltered view can conclude the waitlist is truly empty.
  const showEmptyState =
    !isLoading && entries.length === 0 && !filteredToArtists;

  return (
    <PageLayout title="Waitlist" guard={{ isAllowed: !!user }}>
      {isLoading ? (
        <PageLoading />
      ) : showEmptyState ? (
        <WaitlistEmptyState
          isManagerView={isManagerView}
          onOpenSettings={() => setIsSettingsOpen(true)}
        />
      ) : (
        <>
          {isManagerView && (
            <Stack direction="row" alignItems="center" mb={0}>
              <MultiSelectFilter
                name="artists"
                options={artistOptions}
                state={artistFilterState}
                setState={next =>
                  setSelectedArtistIds(
                    typeof next === 'function' ? next(artistFilterState) : next,
                  )
                }
                emptyLabel="All Artists"
                allLabel="All Artists"
                sx={{ width: 220 }}
              />
            </Stack>
          )}
          <WaitlistTable
            entries={entries}
            totalCount={totalCount}
            isLoading={isLoading}
            showProvider={isManagerView}
            onOpenEntry={entry => setSelectedEntryId(entry.waitlistEntryId)}
            onNotify={entry => setNotifyEntryId(entry.waitlistEntryId)}
            onRemove={onRemove}
            onOpenSettings={() => setIsSettingsOpen(true)}
            onEndReached={onEndReached}
          />
        </>
      )}
      {!!selectedEntryId && (
        <WaitlistEntrySideBarModal
          waitlistEntryId={selectedEntryId}
          showProvider={isManagerView}
          onClose={() => setSelectedEntryId(null)}
          onNotify={entry => setNotifyEntryId(entry.waitlistEntryId)}
          onRemove={onRemove}
        />
      )}
      {notifyEntryId && (
        <ManualNotifyDialog
          waitlistEntryId={notifyEntryId}
          onClose={() => setNotifyEntryId(null)}
        />
      )}
      {isSettingsOpen && (
        <CancellationWaitlistSettingsFormModal
          open
          onClose={() => setIsSettingsOpen(false)}
        />
      )}
    </PageLayout>
  );
}
